import React, { useRef, useEffect } from "react";

const LightRays = ({
  lightSpread = 0.6,
  saturation = 1.3,
  rayLength = 1,
  raysSpeed = 0.6,
  fadeDistance = 1.5,
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: false }); // غیرفعال کردن آلفا برای بهترین عملکرد روی موبایل

    let animationFrameId;
    let width, height;

    const resize = () => {
      const displayWidth = window.innerWidth;
      const displayHeight = Math.min(window.innerHeight, window.screen.height);

      // کاهش تراکم پیکسلی برای سرعت بالا روی موبایل (بدون افت کیفیت به دلیل وجود بلور)
      const scaleFactor = window.innerWidth < 768 ? 0.5 : 0.75;

      width = Math.floor(displayWidth * scaleFactor);
      height = Math.floor(displayHeight * scaleFactor);

      canvas.width = width;
      canvas.height = height;
    };

    window.addEventListener("resize", resize);
    resize();

    const numRays = 14; // تعداد بهینه‌شده برای عدم پردازش سنگین در هر فریم
    const rays = [];

    for (let i = 0; i < numRays; i++) {
      rays.push({
        baseAngle:
          Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.3 * lightSpread,
        width: Math.random() * 80 + 70, // پرتوهای پهن‌تر برای ایجاد مه نوری
        // طول پرتوها رو اینجا محدودتر کردم (۰.۴ تا ۰.۶) تا به پایین صفحه نرسند
        lengthMultiplier:
          (Math.random() * 0.2 + 0.4) * rayLength * fadeDistance,
        speed: (Math.random() * 0.003 + 0.001) * raysSpeed,
        phase: Math.random() * Math.PI * 2,

        swayPhase: Math.random() * Math.PI * 2,
        swaySpeed: (Math.random() * 0.001 + 0.0005) * raysSpeed,

        maxOpacity: Math.random() * 0.12 + 0.03,
        hue: 34 + Math.random() * 10, // زرد طلایی گرم و غبارآلودِ لامپ قدیمی
      });
    }

    const animate = () => {
      // استفاده دقیق از رنگ پس‌زمینه جدید شما
      ctx.fillStyle = "#0c0c0c";
      ctx.fillRect(0, 0, width, height);

      const originX = width / 2;
      const originY = -height * 0.05;

      // ۱. هاله محیطی نرمِ پایه (Ambient Glow) - محدودتر به بالای صفحه
      const ambientGlow = ctx.createRadialGradient(
        originX,
        originY,
        0,
        originX,
        originY,
        height * 0.5, // کاهش شعاع محو شدن از 0.8 به 0.5
      );
      // تطبیق با مشکی #0c0c0c برای پخش شدن و ادغام بی‌نقص
      ambientGlow.addColorStop(0, `hsla(34, ${80 * saturation}%, 30%, 0.23)`);
      ambientGlow.addColorStop(0.3, `hsla(30, ${70 * saturation}%, 12%, 0.05)`);
      ambientGlow.addColorStop(0.7, "rgba(12, 12, 12, 0)"); // خیلی زودتر کاملاً تاریک و محو می‌شود
      ambientGlow.addColorStop(1, "rgba(12, 12, 12, 0)");

      ctx.fillStyle = ambientGlow;
      ctx.fillRect(0, 0, width, height);

      // ۲. پرتوهای متحرک و غبارآلود
      ctx.globalCompositeOperation = "screen";

      rays.forEach((ray) => {
        ray.phase += ray.speed;
        ray.swayPhase += ray.swaySpeed;

        const angle = ray.baseAngle + Math.sin(ray.swayPhase) * 0.03;
        const currentOpacity =
          (Math.sin(ray.phase) * 0.5 + 0.5) * ray.maxOpacity;
        const currentLength = height * ray.lengthMultiplier;

        const endX = originX + Math.cos(angle) * currentLength;
        const endY = originY + Math.sin(angle) * currentLength;

        const anglePerp = angle + Math.PI / 2;
        const spreadFactor = 3.2;

        const p1x = originX + Math.cos(anglePerp) * (ray.width * 0.05);
        const p1y = originY + Math.sin(anglePerp) * (ray.width * 0.05);
        const p2x = originX - Math.cos(anglePerp) * (ray.width * 0.05);
        const p2y = originY - Math.sin(anglePerp) * (ray.width * 0.05);

        const p3x = endX - Math.cos(anglePerp) * (ray.width * spreadFactor);
        const p3y = endY - Math.sin(anglePerp) * (ray.width * spreadFactor);
        const p4x = endX + Math.cos(anglePerp) * (ray.width * spreadFactor);
        const p4y = endY + Math.sin(anglePerp) * (ray.width * spreadFactor);

        const gradient = ctx.createLinearGradient(originX, originY, endX, endY);
        const satVal = Math.min(100, 70 * saturation);

        // فشرده‌سازی گرادیانت: نور را خیلی زودتر (در اواسط پرتو) به صفر می‌رسانیم
        gradient.addColorStop(
          0,
          `hsla(${ray.hue}, ${satVal}%, 55%, ${currentOpacity})`,
        );
        gradient.addColorStop(
          0.4,
          `hsla(${ray.hue}, ${satVal - 10}%, 30%, ${currentOpacity * 0.25})`,
        );
        gradient.addColorStop(0.65, "rgba(12, 12, 12, 0)"); // کاملا در ۶۵ درصد طول پرتو ناپدید می‌شود
        gradient.addColorStop(1, "rgba(12, 12, 12, 0)");

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(p1x, p1y);
        ctx.lineTo(p2x, p2y);
        ctx.lineTo(p3x, p3y);
        ctx.lineTo(p4x, p4y);
        ctx.closePath();
        ctx.fill();
      });

      // ۳. کانون درخشانِ بالای بوم (Bulb Glow)
      const sourceGlow = ctx.createRadialGradient(
        originX,
        originY,
        0,
        originX,
        originY,
        height * 0.22,
      );
      sourceGlow.addColorStop(0, `hsla(42, 100%, 75%, 0.55)`);
      sourceGlow.addColorStop(0.4, `hsla(34, 90%, 55%, 0.18)`);
      sourceGlow.addColorStop(1, "rgba(12, 12, 12, 0)"); // انحلال کامل در تاریکی پس‌زمینه

      ctx.fillStyle = sourceGlow;
      ctx.beginPath();
      ctx.arc(originX, originY, height * 0.22, 0, Math.PI * 2);
      ctx.fill();

      ctx.globalCompositeOperation = "source-over"; // ریست کردن برای فریم بعدی

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [lightSpread, saturation, rayLength, raysSpeed, fadeDistance]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "150vh",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: -10,
        background: "#0c0c0c", // رنگ پس‌زمینه شما برای بخش‌های اطراف بوم
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          // اعمال بلور در سطح پردازنده گرافیکی (GPU) برای روانی بی‌نظیر
          filter: "blur(3px)",
          transform: "scale(1.12)", // جلوگیری از تاریکی در لبه‌های کادر
          willChange: "transform",
        }}
      />
    </div>
  );
};

export default LightRays;
